// Global newsletter-signup popup. The popup markup (snippets/byRavn-newsletter-popup.liquid)
// is rendered once in theme.liquid and opened by any [data-signup-open] trigger — the sticky
// edge tab (snippets/byRavn-signup-tab.liquid) is the storefront one. All listeners are
// delegated, so markup added by AJAX (pagination, filters) keeps working without re-binding.
//
// It is a native <dialog> driven by showModal(): the top layer paints it above every
// z-index on the page (the Zepto customizer's preview overlay sits at 999 and used to cover
// the top of the popup on product pages), and the browser handles the focus trap, the
// inertness of the page behind, and Escape. The fade is CSS, keyed off [open], so opening
// and closing is showModal()/close() with no state of ours to unwind.
//
// Scroll lock follows the cart-gavepose-modal pattern (assets/byRavn-gavepose.js): the two
// popups share `body.overflow-hidden`, so closing one must not unlock scroll while the
// cart drawer is open.
(function () {
  const MODAL_ID = 'byRavnSignupModal';
  // Auto-open (theme settings → Signup popup → "Auto-open delay", 0 = off):
  // AUTO_FLAG (session) = shown/opened once this browser session;
  // COMPLETED_FLAG (local) = signed up successfully, never auto-open again.
  const AUTO_FLAG = 'byRavnSignupAutoShown';
  const COMPLETED_FLAG = 'byRavnSignupCompleted';
  // Dismissing the tab with its cross hides it for the rest of the browser session only —
  // a completed signup (COMPLETED_FLAG) is what retires it for good.
  const TAB_DISMISSED_FLAG = 'byRavnSignupTabDismissed';
  let modalTrigger = null;
  let autoTimer = null;

  function modal() {
    return document.getElementById(MODAL_ID);
  }

  function tab() {
    return document.querySelector('[data-signup-tab]');
  }

  function tabToggle() {
    const el = tab();
    return el && el.querySelector('[data-signup-open]');
  }

  function setTabExpanded(expanded) {
    const toggle = tabToggle();
    if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function dismissTab() {
    const el = tab();
    if (!el) return;
    // Hand focus over before hiding: leaving it on a display:none button strands keyboard
    // users, who then tab from a control that is no longer on the page. #MainContent
    // carries tabindex="-1" in theme.liquid for exactly this. preventScroll keeps the
    // shopper where they were reading.
    if (el.contains(document.activeElement)) {
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
      else document.activeElement.blur();
    }
    el.hidden = true;
    try {
      sessionStorage.setItem(TAB_DISMISSED_FLAG, '1');
    } catch (e) {}
  }

  // The tab ships `hidden` so a dismissed one never flashes before this runs. In the theme
  // editor it always shows, otherwise byRavn could not see the tab they are configuring.
  function revealTab() {
    const el = tab();
    if (!el) return;
    if (!window.Shopify?.designMode) {
      try {
        if (sessionStorage.getItem(TAB_DISMISSED_FLAG) === '1') return;
        if (localStorage.getItem(COMPLETED_FLAG) === '1') return;
      } catch (e) {}
    }
    el.hidden = false;
  }

  function drawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.classList.contains('active');
  }

  function open(trigger) {
    const el = modal();
    if (!el || el.open || typeof el.showModal !== 'function') return;
    modalTrigger = trigger || null;
    el.showModal();
    document.body.classList.add('overflow-hidden');
    setTabExpanded(true);
    // showModal() lands focus on the card, the first focusable thing in the dialog thanks
    // to its tabindex="-1" — a screen reader then reads the popup from the top, and no
    // mobile keyboard springs up from a focused input. Override that only for the
    // post-submit state, where the success message is what the shopper came back for.
    const success = el.querySelector('[data-signup-success]');
    if (success) success.focus();
  }

  function close() {
    const el = modal();
    if (!el || !el.open) return;
    el.close();
    releasePage();
  }

  // What the open popup held on to. Called on our own close path and again from the dialog's
  // `close` event, which covers the paths we do not drive (a stray close() from elsewhere):
  // running twice is harmless, leaving the page scroll-locked is not.
  function releasePage() {
    if (!drawerOpen()) document.body.classList.remove('overflow-hidden');
    setTabExpanded(false);
    // Focus goes back only now that the dialog is closed: while it is open the rest of the
    // page is inert and the tab cannot take focus.
    if (!modalTrigger) return;
    modalTrigger.focus();
    modalTrigger = null;
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    if (event.target.closest('[data-signup-tab-dismiss]')) {
      event.preventDefault();
      dismissTab();
      return;
    }
    const opener = event.target.closest('[data-signup-open]');
    if (opener) {
      event.preventDefault();
      // A manual open counts as "seen this session": cancel any pending
      // auto-open so the popup can't come back after the shopper closes it.
      if (autoTimer) {
        clearTimeout(autoTimer);
        autoTimer = null;
      }
      try {
        sessionStorage.setItem(AUTO_FLAG, '1');
      } catch (e) {}
      open(opener);
      return;
    }
    if (event.target.closest('[data-signup-close]')) {
      event.preventDefault();
      close();
    }
  });

  function bindDialog(el) {
    // Escape is the browser's to handle; this only mops up around it. Closing here rather
    // than from the `close` event keeps the popup working in browsers that never fire it.
    el.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      // Dawn's CartDrawer and CartNotification close themselves on Escape *keyup*. Escape
      // reaches this dialog on keydown, so without this the trailing keyup would reach the
      // drawer underneath and close that too — one press, two things closed.
      document.addEventListener('keyup', (keyup) => keyup.key === 'Escape' && keyup.stopPropagation(), {
        capture: true,
        once: true,
      });
      close();
    });

    // Dismiss only when the press both starts and ends on the dimmed area outside the card.
    // A `click` targets the nearest common ancestor of its press and release, so testing the
    // click alone treats a drag that ends outside the card — an aborted drag, or selecting
    // the popup text — as a click outside and closes the popup out from under the shopper.
    let pressStartedOutside = false;
    el.addEventListener('pointerdown', (event) => {
      pressStartedOutside = event.target === el;
    });
    el.addEventListener('pointerup', (event) => {
      if (pressStartedOutside && event.target === el) close();
    });

    el.addEventListener('close', releasePage);
  }

  // Re-open the popup after the shopper submits THIS popup's own form (the native POST
  // reloads the page), so they see the success/error state instead of a silent reload.
  // We must NOT key off Shopify's `customer_posted=true` param or `form.posted_successfully?`
  // — both are page-global and become true after ANY {% form 'customer' %} submit on the
  // site (e.g. the footer newsletter, which renders on every page), which would pop this
  // discount modal open unprompted. A sessionStorage flag set on this specific form's
  // submit is unique to the popup.
  const SUBMIT_FLAG = 'byRavnSignupSubmitted';

  document.addEventListener('submit', (event) => {
    if (event.target && event.target.id === 'byRavnSignupForm') {
      try {
        sessionStorage.setItem(SUBMIT_FLAG, '1');
      } catch (e) {}
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const el = modal();
    if (!el) return;
    bindDialog(el);
    let submitted = false;
    try {
      submitted = sessionStorage.getItem(SUBMIT_FLAG) === '1';
      if (submitted) sessionStorage.removeItem(SUBMIT_FLAG);
    } catch (e) {}
    if (submitted) {
      open(null);
      // The success message only renders on the page-load right after a
      // successful POST — remember it so we never auto-open for this shopper.
      if (el.querySelector('[data-signup-success]')) {
        try {
          localStorage.setItem(COMPLETED_FLAG, '1');
        } catch (e) {}
      }
    }
    // After the COMPLETED_FLAG write above, so a shopper who just signed up never
    // gets the tab back.
    revealTab();
    if (!submitted) scheduleAutoOpen(el);
  });

  // Auto-open after the delay set in theme settings (data-auto-open, seconds).
  // Skipped in the theme editor, when already shown this session, and after a
  // completed signup. If the cart drawer is open when the timer fires, the
  // popup yields (no flag set), so it simply tries again on the next page.
  function scheduleAutoOpen(el) {
    const delay = Number.parseInt(el.dataset.autoOpen || '0', 10);
    if (!delay || delay < 1) return;
    if (window.Shopify?.designMode) return;
    try {
      if (sessionStorage.getItem(AUTO_FLAG) === '1') return;
      if (localStorage.getItem(COMPLETED_FLAG) === '1') return;
    } catch (e) {}
    autoTimer = setTimeout(() => {
      autoTimer = null;
      if (el.open) return;
      if (drawerOpen()) return;
      try {
        sessionStorage.setItem(AUTO_FLAG, '1');
      } catch (e) {}
      open(null);
    }, delay * 1000);
  }
})();
